import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Package, Heart, LogOut, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfilePage = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('orders');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    fetchOrders();
  }, [isAuthenticated, navigate]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-500';
      case 'processing': return 'text-blue-500';
      case 'shipped': return 'text-purple-500';
      case 'delivered': return 'text-green-500';
      case 'cancelled': return 'text-red-500';
      default: return 'text-foreground/60';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'En attente';
      case 'processing': return 'En préparation';
      case 'shipped': return 'Expédiée';
      case 'delivered': return 'Livrée';
      case 'cancelled': return 'Annulée';
      default: return status;
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="profile-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="py-12 border-b border-black/10 mb-12">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-heading text-4xl md:text-6xl tracking-tight"
          >
            Mon Compte
          </motion.h1>
          <p className="text-foreground/60 mt-4">
            Bienvenue, {user.first_name}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <nav className="space-y-2">
              <button
                onClick={() => setActiveTab('orders')}
                className={`w-full flex items-center gap-3 px-4 py-3 transition-colors ${
                  activeTab === 'orders' 
                    ? 'bg-gold text-black' 
                    : 'hover:bg-black/5 text-foreground/70'
                }`}
                data-testid="tab-orders"
              >
                <Package size={18} />
                Mes commandes
              </button>
              <button
                onClick={() => navigate('/wishlist')}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-black/5 text-foreground/70 transition-colors"
                data-testid="tab-wishlist"
              >
                <Heart size={18} />
                Mes favoris
              </button>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-black/5 text-foreground/70 transition-colors"
                data-testid="logout-btn"
              >
                <LogOut size={18} />
                Déconnexion
              </button>
            </nav>

            {/* User Info */}
            <div className="mt-8 p-6 bg-white border border-black/10">
              <h3 className="font-heading text-lg mb-4">Informations</h3>
              <div className="space-y-2 text-sm">
                <p className="text-foreground/60">
                  <span className="text-foreground">{user.first_name} {user.last_name}</span>
                </p>
                <p className="text-foreground/60">{user.email}</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            {activeTab === 'orders' && (
              <div data-testid="orders-section">
                <h2 className="font-heading text-2xl mb-6">Mes commandes</h2>
                
                {loading ? (
                  <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-24 bg-white animate-pulse" />
                    ))}
                  </div>
                ) : orders.length === 0 ? (
                  <div className="text-center py-12 bg-white">
                    <Package size={48} className="text-foreground/20 mx-auto mb-4" />
                    <p className="text-foreground/60">Aucune commande pour le moment</p>
                    <button
                      onClick={() => navigate('/shop')}
                      className="btn-primary mt-6"
                    >
                      Découvrir la boutique
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {orders.map((order) => (
                      <motion.div
                        key={order.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white p-6 border border-black/10 hover:border-gold/30 transition-colors cursor-pointer"
                        onClick={() => navigate(`/order/${order.id}`)}
                        data-testid={`order-${order.id}`}
                      >
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div>
                            <p className="text-xs text-foreground/40 uppercase tracking-wider">
                              Commande #{order.id.slice(0, 8)}
                            </p>
                            <p className="font-heading text-lg mt-1">
                              {order.items.length} article{order.items.length > 1 ? 's' : ''}
                            </p>
                            <p className="text-sm text-foreground/60 mt-1">
                              {new Date(order.created_at).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              })}
                            </p>
                          </div>
                          <div className="flex items-center gap-6">
                            <div className="text-right">
                              <p className={`text-sm uppercase tracking-wider ${getStatusColor(order.status)}`}>
                                {getStatusText(order.status)}
                              </p>
                              <p className="price-tag text-gold text-lg mt-1">
                                {order.total.toFixed(2)} €
                              </p>
                            </div>
                            <ChevronRight size={20} className="text-foreground/40" />
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
